import { useMemo, useState, type FormEvent } from 'react';
import { Card, StatusCard } from '@/components/ui/card';
import { SectionLabel } from '@/components/ui/section-label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { useTranslation } from '@/hooks/useTranslation';
import { AdminApiClient } from '@/features/admin/api/AdminApiClient';
import type { EmptyResponse, Result } from '@/lib/http/HttpClient';
import { useApiClient } from '@/hooks/useApiClient';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { cn } from '@/lib/utils';
import { useErrorHandler } from '@/hooks/useErrorHandler';
import { ShieldAlert, Package } from 'lucide-react';
import { ALL_MODULE_KEYS, TRACEABILITY } from '@/constants/modules';

type RegisterFormProps = {
  className?: string;
  lockedCompany?: string;
};

export const RegisterForm: React.FC<RegisterFormProps> = ({ className = '', lockedCompany }) => {
  const http = useApiClient();
  const { t } = useTranslation();
  const { tokenService } = useAuth();
  const { errorMessage, handleError, clearError } = useErrorHandler('RegisterForm');

  // Rights ceiling: an admin may only grant modules they hold and cannot create admins;
  // super-admin keeps the full canonical offer. The backend enforces this too.
  const isSuperAdmin = (tokenService?.getRole() ?? 'user') === 'super-admin';
  const offeredModuleKeys = useMemo<string[]>(
    () => (isSuperAdmin ? ALL_MODULE_KEYS : ((tokenService?.getModules() ?? []) as string[])),
    [isSuperAdmin, tokenService]
  );
  const defaultModules = useMemo(
    () => [TRACEABILITY].filter((key) => offeredModuleKeys.includes(key)),
    [offeredModuleKeys]
  );

  const [email, setEmail] = useState<string>('');
  const [firstName, setFirstName] = useState<string>('');
  const [lastName, setLastName] = useState<string>('');
  const [entreprise, setEntreprise] = useState(lockedCompany ?? '');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [modules, setModules] = useState<string[]>(defaultModules);
  const [admin, setAdmin] = useState(false);
  const [result, setResult] = useState<Result<EmptyResponse> | null>(null);

  const adminApiClient = useMemo(() => (http ? new AdminApiClient(http) : null), [http]);

  const moduleOptions = useMemo(
    () =>
      offeredModuleKeys.map((key) => ({
        key,
        labelKey: `modules.labels.${key}`,
        descriptionKey: `modules.descriptions.${key}`,
      })),
    [offeredModuleKeys]
  );

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    if (!adminApiClient) {
      return;
    }

    clearError();
    setResult(null);

    if (password !== confirmPassword) {
      handleError(new Error(t('admin.registerForm.errors.passwordMismatch')));
      return;
    }

    adminApiClient
      .createUser({
        email,
        firstName,
        lastName,
        companyName: entreprise,
        role: isSuperAdmin && admin ? 'admin' : 'user',
        modules,
        password,
      })
      .then((res) => {
        setResult(res);
        if (res.ok) {
          setEmail('');
          setFirstName('');
          setLastName('');
          setEntreprise('');
          setPassword('');
          setConfirmPassword('');
          setModules(defaultModules);
          setAdmin(false);
        } else {
          handleError(new Error(res.message || t('admin.registerForm.errors.default')));
        }
      })
      .catch((err) => {
        handleError(err);
      });
  }

  const toggleModule = (key: string) => {
    setModules((prev) =>
      prev.includes(key) ? prev.filter((module) => module !== key) : [...prev, key]
    );
  };

  return (
    <form onSubmit={handleSubmit} className={cn('w-full', className)}>
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1.4fr_1fr]">
        {/* Left: identity + credentials */}
        <div className="flex flex-col gap-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="admin-first-name">
                {t('admin.registerForm.fields.firstName.label')}
              </Label>
              <Input
                id="admin-first-name"
                type="text"
                placeholder={t('admin.registerForm.fields.firstName.placeholder')}
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="admin-last-name">
                {t('admin.registerForm.fields.lastName.label')}
              </Label>
              <Input
                id="admin-last-name"
                type="text"
                placeholder={t('admin.registerForm.fields.lastName.placeholder')}
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                required
              />
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="admin-email">{t('admin.registerForm.fields.email.label')}</Label>
              <Input
                id="admin-email"
                type="email"
                placeholder={t('admin.registerForm.fields.email.placeholder')}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="admin-company">{t('admin.registerForm.fields.company.label')}</Label>
              {lockedCompany ? (
                <Input
                  id="admin-company"
                  type="text"
                  value={lockedCompany}
                  readOnly
                  disabled
                  className="cursor-not-allowed opacity-60"
                />
              ) : (
                <Input
                  id="admin-company"
                  type="text"
                  placeholder={t('admin.registerForm.fields.company.placeholder')}
                  value={entreprise}
                  onChange={(e) => setEntreprise(e.target.value)}
                  required
                />
              )}
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="admin-password">
                {t('admin.registerForm.fields.password.label')}
              </Label>
              <Input
                id="admin-password"
                type="password"
                placeholder={t('admin.registerForm.fields.password.placeholder')}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="admin-password-confirm">
                {t('admin.registerForm.fields.confirmPassword.label')}
              </Label>
              <Input
                id="admin-password-confirm"
                type="password"
                placeholder={t('admin.registerForm.fields.confirmPassword.placeholder')}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
              />
            </div>
          </div>
        </div>

        {/* Right: role + modules + submit */}
        <div className="flex flex-col gap-4">
          {/* Role — only a super-admin may promote to admin */}
          {isSuperAdmin && (
          <Card tier="muted" padding="sm">
            <SectionLabel icon={ShieldAlert}>{t('admin.registerForm.role.title')}</SectionLabel>
            <div className="mt-3 flex items-center justify-between gap-3">
              <p className="text-xs text-muted-foreground">
                {t('admin.registerForm.role.description')}
              </p>
              <div className="flex items-center gap-2 shrink-0">
                <Checkbox
                  id="admin-role"
                  checked={admin}
                  onCheckedChange={(checked) => setAdmin(checked === true)}
                />
                <Label htmlFor="admin-role" className="text-sm font-medium">
                  {t('admin.registerForm.role.adminLabel')}
                </Label>
              </div>
            </div>
          </Card>
          )}

          {/* Modules */}
          <Card tier="muted" padding="sm">
            <SectionLabel icon={Package}>{t('admin.registerForm.modules.title')}</SectionLabel>
            <div className="mt-3 flex flex-col gap-2">
              {moduleOptions.map(({ key, labelKey, descriptionKey }) => {
                const isChecked = modules.includes(key);
                return (
                  <div key={key} className="flex items-center gap-3">
                    <Checkbox
                      id={`module-${key}`}
                      checked={isChecked}
                      onCheckedChange={() => toggleModule(key)}
                    />
                    <label
                      htmlFor={`module-${key}`}
                      className={cn(
                        'flex-1 cursor-pointer rounded-lg border px-3 py-2 text-sm shadow-sm transition',
                        isChecked
                          ? 'border-primary/60 bg-primary/10'
                          : 'border-border bg-card hover:border-primary/40'
                      )}
                    >
                      <p className="font-medium text-foreground">{t(labelKey)}</p>
                      <p className="text-xs text-muted-foreground">{t(descriptionKey)}</p>
                    </label>
                  </div>
                );
              })}
            </div>
          </Card>

          {/* Submit + status */}
          <div className="flex flex-col gap-3">
            <Button type="submit" className="w-full">
              {t('admin.registerForm.submit')}
            </Button>
            {result && !result?.ok && (
              <StatusCard
                type="error"
                message={result?.message ?? t('admin.registerForm.errors.default')}
                className="self-stretch"
              />
            )}
            {result?.ok && (
              <StatusCard
                type="success"
                message={t('admin.registerForm.success')}
                className="self-stretch"
              />
            )}
            {errorMessage && (
              <StatusCard type="error" message={errorMessage} className="self-stretch" />
            )}
          </div>
        </div>
      </div>
    </form>
  );
};
